import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getPhoneLengthRule } from "../../applicant/utils/contactFieldValidation";
import type { ContractConcept, DocumentRequest } from "./participant-contract";
import contractData from "../data/participant-app-contract.json";

export type Entry = Record<string, string>;
export type AnswerValue = string | Entry | Entry[] | string[];
export type Answers = Record<string, AnswerValue>;
export function editablePassportField(id: string): boolean {
  return id === "pf.identity.nationalities" || id === "pf.passport.issuing_country";
}

export function mergeSuppliedAnswers(current: Answers, supplied: Answers, previous: Answers, editedFields: ReadonlySet<string> = new Set()): Answers {
  const next = {...current};
  for (const [id, value] of Object.entries(supplied)) {
    if (editedFields.has(id)) continue;
    // Keep a participant's correction when the contextual default has not changed.
    if (editablePassportField(id) && current[id] && current[id] !== previous[id] && value === previous[id]) continue;
    next[id] = value;
  }
  return next;
}
export type Applicability = true | false | "unknown";

export const postalAddressContract = contractData.postal_address_contract;
type AddressTemplate = typeof postalAddressContract.countries.US;
/** Used when a country has no published address layout. Postal code is the PIN field. */
const standardAddressTemplate: AddressTemplate = {
  source: "",
  fields: [
    { id: "line1", label: "Street and number", required: true },
    { id: "line2", label: "Building, apartment or barrio", required: false },
    { id: "locality", label: "City or municipality", required: true },
    { id: "administrative_area", label: "Department or capital district", required: false },
    { id: "postal_code", label: "Postal code", required: true },
  ],
  line_groups: [["line1", "line2"], ["locality", "administrative_area", "postal_code"]],
};
export function addressTemplate(country: string | undefined): AddressTemplate | undefined {
  const code = (country ?? "").trim().toUpperCase();
  if (!code) return undefined;
  return (postalAddressContract.countries as Record<string, AddressTemplate>)[code] ?? standardAddressTemplate;
}

export function addressDraft(value: AnswerValue | undefined): Entry {
  if (typeof value === "string" && value.trim()) return { mode: "freeform", freeform: value, country: "" };
  return { mode: "structured", ...record(value) };
}

export function addressPayload(value: AnswerValue): AnswerValue {
  if (typeof value === "string") return value;
  const draft = addressDraft(value);
  const mode = draft.mode === "freeform" || !addressTemplate(draft.country) ? "freeform" : "structured";
  const keys = postalAddressContract.representations[mode];
  return Object.fromEntries(keys.map((key) => [key, key === "mode" ? mode : draft[key] ?? ""]));
}

export function addressError(value: AnswerValue | undefined, required: boolean): string | null {
  if (typeof value === "string") return required && !value.trim() ? "Enter your address." : null;
  const draft = addressDraft(value);
  if (!required && !Object.entries(draft).some(([key, text]) => key !== "mode" && text.trim())) return null;
  if (!draft.country) return "Choose the country where you live.";
  const template = addressTemplate(draft.country);
  if (draft.mode === "freeform" || !template) return draft.freeform?.trim() ? null : "Enter your complete address.";
  const missing = template.fields.find((field) => field.required && !draft[field.id]?.trim());
  return missing ? `Enter ${missing.label.toLowerCase()}.` : null;
}

export function scalar(value: AnswerValue | undefined): string {
  if (typeof value === "string") return value;
  if (value && !Array.isArray(value)) return value.value ?? "";
  return "";
}

export function entries(value: AnswerValue | undefined): Entry[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter((item): item is Entry => typeof item === "object" && item !== null);
}

export function record(value: AnswerValue | undefined): Entry {
  return value && !Array.isArray(value) && typeof value === "object" ? value : {};
}

// Only the comparison grammar authored in the imported contract is supported.
// Missing answers and unsupported expressions remain unknown, never false.
export function applicability(item: Pick<ContractConcept, "applicability">, answers: Answers, concepts: ContractConcept[]): Applicability {
  const rule = item.applicability;
  if (!rule || rule.machine_status === "always_applies") return true;
  if (!rule.machine) return "unknown";
  const byKey = Object.fromEntries(concepts.map((concept) => [concept.answer_key, concept.id]));
  const groups = rule.machine.split(/\s+OR\s+/).map((group) => {
    const conditions = group.split(/\s+AND\s+/).map((part): Applicability => {
      const match = part.trim().match(/^([\w.[\]]+)\s*(!=|=)\s*([\w-]+)$/);
      if (!match) return "unknown";
      const value = scalar(answers[byKey[match[1]]]);
      if (!value) return "unknown";
      return match[2] === "=" ? value === match[3] : value !== match[3];
    });
    return conditions.includes(false) ? false : conditions.includes("unknown") ? "unknown" : true;
  });
  return groups.includes(true) ? true : groups.includes("unknown") ? "unknown" : false;
}

export function isRequired(concept: ContractConcept, answers: Answers, concepts: ContractConcept[]) {
  return concept.requiredness === "required" || (concept.requiredness === "conditionally_required" && applicability(concept, answers, concepts) === true);
}

export function answerError(concept: ContractConcept, answers: Answers, concepts: ContractConcept[]): string | null {
  if (applicability(concept, answers, concepts) !== true) return null;
  const value = answers[concept.id];
  const text = scalar(value).trim();
  const required = isRequired(concept, answers, concepts);
  if (concept.answer.shape === "postal_address") return addressError(value, required);
  if (concept.answer.shape === "structured_entry") {
    return required && !entries(value).length ? "Add an entry to complete this field." : null;
  }
  if (concept.answer.shape === "date_interval") {
    const { start = "", end = "" } = record(value);
    if ((required || start || end) && (!start || !end)) return "Enter both dates.";
    if (start && end && end < start) return "End date must be on or after start date.";
    return null;
  }
  if (concept.answer.shape === "monetary_amount") {
    const { amount = "", currency = "" } = record(value);
    if ((required || amount || currency) && (!amount || !currency)) return "Enter the amount and currency.";
    if (amount && (!Number.isFinite(Number(amount)) || Number(amount) < 0)) return "Enter a valid amount.";
    if (currency && !/^[A-Z]{3}$/.test(currency)) return "Use a three-letter currency code.";
    return null;
  }
  if (required && !text) return "This field is required.";
  if (!text) return null;
  if (concept.answer.shape === "email_address" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return "Enter a valid email address.";
  if (concept.answer.shape === "telephone_number") {
    const parsed = parsePhoneNumberFromString(text);
    const national = String(parsed?.nationalNumber || text.replace(/\D/g, ""));
    const { min, max } = getPhoneLengthRule(parsed?.countryCallingCode || "");
    if (national.length < min || national.length > max) {
      return min === max ? `Enter ${max} digits.` : `Enter ${min} to ${max} digits.`;
    }
  }
  if (concept.answer.shape === "single_choice" && concept.answer.options?.length && !concept.answer.options.some((option) => option.value === text)) return "Choose one of the available options.";
  return null;
}

// Internal IDs keep presentation stable; the developer payload uses KB answer keys.
// False/unknown applicability is excluded without deleting the user's saved draft.
export function toAnswerKeyPayload(answers: Answers, concepts: ContractConcept[]) {
  return Object.fromEntries(concepts.filter((concept) => answers[concept.id] !== undefined && applicability(concept, answers, concepts) === true).map((concept) => {
    const value = answers[concept.id];
    const output = concept.answer.shape === "country_reference" && concept.answer.cardinality === "many"
      ? scalar(value).split(",").filter(Boolean)
      : concept.answer.shape === "boolean_with_explanation" && scalar(value) === "no"
        ? { value: "no", explanation: "" }
        : concept.answer.shape === "postal_address" ? addressPayload(value) : value;
    return [concept.answer_key, output];
  }));
}

export function documentCompletion(documents: DocumentRequest[], files: Record<string, string>, answers: Answers, concepts: ContractConcept[]) {
  const unresolved = documents.some((document) => document.requiredness === "conditionally_required" && applicability(document, answers, concepts) === "unknown");
  const required = documents.filter((document) => document.requiredness !== "optional" && applicability(document, answers, concepts) === true);
  return !unresolved && required.every((document) => Boolean(files[document.id]));
}
